import { GameEvent, OperatorEvent } from "../interfaces/theos.interface";
import { LogLevel, log  } from "../utils/log";
import { closeExternalRealityCheck, showExternalRealityCheck } from "./realityCheckIframe";
import { IPostMessageApi } from "../interfaces/postMessageApi.interface";
import { handleClientSideRcInterval } from "./handleIdlePause";
import { postToOperator } from "../feim/postToOperator";

/**
 * An adapter for the legacy RC API, so Theos layer is not needed when using it.
 * Will not get any new features, but some environments use this for reality check.
 */

interface ILegacyMessage {
    readonly method: string;
    readonly params: {
        readonly [key: string]: unknown;
        callback?: string;
        return_data?: string[];
        url?: string;
        // hanshake success callback
        success?: string;
    };
}

const callbacks: {
    gamePausedCallback?: string;
    gameResumedCallback?: string;
} = {};

// Directly post a legacy RC message
const post = (method: string, params?: unknown): void =>
{
    postToOperator({method, params});
};

export class LegacyApiAdapter implements IPostMessageApi
{
    public name = "Legacy RC";

    public constructor ()
    {
        post("gameLoadedHandler");
    }

    public checkHandshake (message: unknown): boolean
    {
        const data = message as ILegacyMessage;

        if (data.method === "operatorLoaded") {
            // In case the operator loads itself after the game (if it lives inside the rciframe for example)
            post("gameLoadedHandler");
        }
        else if (data.method === "confirmHandshake") {
            log("Using direct Legacy PostMessage API connection", LogLevel.INIT);

            handleClientSideRcInterval();

            if (data.params.success !== undefined) {
                post(data.params.success);
            }
            return true;
        }

        return false;
    }

    public processInbound (data: unknown): {feimOpEvent: OperatorEvent; payload?: {}} | undefined
    {
        if (data === undefined) {
            return;
        }

        const eventData = data as ILegacyMessage;

        if (eventData.method === undefined)
        {
            return;
        }

        // Events that need to reach the game are converted to FEIM format so all the listeners work
        switch (eventData.method)
        {
            case "pauseGame":
                callbacks.gamePausedCallback = eventData.params?.callback;
                return {feimOpEvent: OperatorEvent._PAUSE_WHEN_IDLE, payload: {realityCheck: true}};
            case "resumeGame":
                callbacks.gameResumedCallback = eventData.params?.callback;
                return {feimOpEvent: OperatorEvent._RESUME};
            case "navigateTo": {
                const payload = eventData.params as {url: string};

                return {feimOpEvent: OperatorEvent._NAVIGATE_GAME, payload};
            }
        }

        return undefined;
    }

    public processOutbound (event: GameEvent, _payload: unknown): { method: string; params?: unknown } | undefined
    {
        switch (event) {
            // Internal events
            case GameEvent.PAUSED: {
                showExternalRealityCheck();
                if (callbacks.gamePausedCallback !== undefined) {
                    return {method: callbacks.gamePausedCallback};
                }
                return;
            }
            case GameEvent.RESUMING_AFTER_PAUSE: {
                closeExternalRealityCheck();
                if (callbacks.gameResumedCallback !== undefined) {
                    return {method: callbacks.gameResumedCallback};
                }
                return;
            }
            case GameEvent.REPORT_SESSION_DETAILS: {
                const payload = _payload as {sessionTime: number};

                return {method: "playTimeHandler", params: {delay: payload.sessionTime}};
            }
            // Legacy API will not get any new features (events), so it's fine to return NOT_APPLICABLE as default
            default: return;
        }
    }
}